import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import {
  CreatePresetRequest,
  PresetTableInput,
  PresetInfo,
  PresetJsonStructure,
  DatabaseTableInfo
} from '../../models/configuration-preset.model';

@Component({
  selector: 'app-configuration-presets',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './configuration-presets.component.html',
  styleUrls: ['./configuration-presets.component.scss']
})
export class ConfigurationPresetsComponent implements OnInit {
  // Data
  presets: PresetInfo[] = [];
  databases: string[] = [];

  // Loading states
  loading = false;
  error: string | null = null;
  successMessage: string | null = null;

  // Create Preset Modal
  showCreateModal = false;
  presetName = '';
  selectedDatabase = '';
  presetTables: PresetTableInput[] = [];

  // Add Table to Preset
  showAddTableModal = false;
  databaseTables: DatabaseTableInfo[] = [];
  tablesLoading = false;
  tableSearchText = '';
  filteredTables: DatabaseTableInfo[] = [];
  showTableSuggestions = false;
  newTableSchema = 'dbo';
  newTableName = '';
  newTableType = '';

  // View Details Modal
  showDetailsModal = false;
  selectedPresetDetails: PresetJsonStructure | null = null;

  // Delete confirmation
  showDeleteModal = false;
  presetToDelete: PresetInfo | null = null;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadDatabases();
    this.loadPresets();
  }

  loadDatabases(): void {
    this.apiService.getDatabases().subscribe({
      next: (databases) => {
        this.databases = databases;
      },
      error: (err) => {
        console.error('Failed to load databases:', err);
      }
    });
  }

  loadPresets(): void {
    this.loading = true;
    this.error = null;
    this.apiService.getPresets().subscribe({
      next: (presets) => {
        this.presets = presets;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load presets: ' + (err.error?.error || err.message);
        this.loading = false;
      }
    });
  }

  // Create Preset Modal
  openCreateModal(): void {
    this.showCreateModal = true;
    this.presetName = '';
    this.selectedDatabase = '';
    this.presetTables = [];
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  onDatabaseChange(): void {
    this.presetTables = [];
    if (this.selectedDatabase) {
      this.loadDatabaseTables();
    }
  }

  loadDatabaseTables(): void {
    if (!this.selectedDatabase) return;

    this.tablesLoading = true;
    this.apiService.getDatabaseTables(this.selectedDatabase).subscribe({
      next: (tables) => {
        this.databaseTables = tables;
        this.filterTables();
        this.tablesLoading = false;
      },
      error: (err) => {
        console.error('Failed to load tables:', err);
        this.tablesLoading = false;
      }
    });
  }

  openAddTableModal(): void {
    if (!this.selectedDatabase) {
      this.error = 'Please select a database first';
      return;
    }
    this.showAddTableModal = true;
    this.newTableSchema = 'dbo';
    this.newTableName = '';
    this.newTableType = '';
    this.tableSearchText = '';
    this.loadDatabaseTables();
  }

  closeAddTableModal(): void {
    this.showAddTableModal = false;
  }

  filterTables(): void {
    const searchLower = this.tableSearchText.toLowerCase().trim();
    this.filteredTables = this.databaseTables.filter(t =>
      t.tableName.toLowerCase().includes(searchLower) ||
      t.schemaName.toLowerCase().includes(searchLower)
    ).slice(0, 20);
  }

  onTableSearchChange(): void {
    this.filterTables();
    this.showTableSuggestions = true;
  }

  selectTable(table: DatabaseTableInfo): void {
    this.newTableName = table.tableName;
    this.newTableSchema = table.schemaName;
    this.tableSearchText = `${table.schemaName}.${table.tableName}`;
    this.showTableSuggestions = false;
  }

  addTableToPreset(): void {
    if (!this.newTableName || !this.newTableType) {
      this.error = 'Please fill in all fields';
      return;
    }

    // Check if table already added
    const exists = this.presetTables.some(t =>
      t.tableName === this.newTableName && t.schemaName === this.newTableSchema
    );

    if (exists) {
      this.error = 'This table is already added to the preset';
      return;
    }

    this.presetTables.push({
      tableName: this.newTableName,
      schemaName: this.newTableSchema,
      type: this.newTableType
    });

    this.closeAddTableModal();
    this.showSuccess('Table added to preset');
  }

  removeTableFromPreset(index: number): void {
    this.presetTables.splice(index, 1);
  }

  savePreset(): void {
    if (!this.presetName) {
      this.error = 'Please enter a preset name';
      return;
    }

    if (!this.selectedDatabase) {
      this.error = 'Please select a database';
      return;
    }

    if (this.presetTables.length === 0) {
      this.error = 'Please add at least one table';
      return;
    }

    const request: CreatePresetRequest = {
      presetName: this.presetName,
      databaseName: this.selectedDatabase,
      tables: this.presetTables
    };

    this.loading = true;
    this.apiService.createPreset(request).subscribe({
      next: (result) => {
        if (result.success) {
          this.showSuccess(result.message);
          this.loadPresets();
          this.closeCreateModal();
        } else {
          this.error = result.message;
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to create preset: ' + (err.error?.error || err.message);
        this.loading = false;
      }
    });
  }

  // View Details
  viewPresetDetails(preset: PresetInfo): void {
    this.loading = true;
    this.apiService.getPresetDetails(preset.presetName).subscribe({
      next: (details) => {
        this.selectedPresetDetails = details;
        this.showDetailsModal = true;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load preset details: ' + (err.error?.error || err.message);
        this.loading = false;
      }
    });
  }

  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedPresetDetails = null;
  }

  // Download Preset
  downloadPreset(preset: PresetInfo): void {
    this.apiService.downloadPreset(preset.presetName).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${preset.presetName}.json`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.showSuccess('Preset downloaded successfully');
      },
      error: (err) => {
        this.error = 'Failed to download preset: ' + (err.error?.error || err.message);
      }
    });
  }

  // Regenerate Preset
  regeneratePreset(preset: PresetInfo): void {
    if (!confirm(`Are you sure you want to regenerate "${preset.presetName}"? This will update the preset with current database data.`)) {
      return;
    }

    this.loading = true;
    this.apiService.regeneratePreset(preset.presetName).subscribe({
      next: (result) => {
        if (result.success) {
          this.showSuccess(result.message);
          this.loadPresets();
        } else {
          this.error = result.message;
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to regenerate preset: ' + (err.error?.error || err.message);
        this.loading = false;
      }
    });
  }

  // Delete Preset
  confirmDeletePreset(preset: PresetInfo): void {
    this.presetToDelete = preset;
    this.showDeleteModal = true;
  }

  cancelDeletePreset(): void {
    this.showDeleteModal = false;
    this.presetToDelete = null;
  }

  executeDeletePreset(): void {
    if (!this.presetToDelete) return;

    this.apiService.deletePreset(this.presetToDelete.presetName).subscribe({
      next: () => {
        this.loadPresets();
        this.cancelDeletePreset();
        this.showSuccess('Preset deleted successfully');
      },
      error: (err) => {
        this.error = 'Failed to delete preset: ' + (err.error?.error || err.message);
      }
    });
  }

  // Utility
  showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => this.successMessage = null, 3000);
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

